// System settings editor (per-region key/value). Mirrors the Email templates
// page but simpler — a plain value field and no cross-region sync (values are
// region-local). Well-known key: `runner.targetVersion` drives the customer
// Runners page "update available" indicator.

import { useEffect, useMemo, useState } from 'react';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import { Button } from '@progress/kendo-react-buttons';
import { Input } from '@progress/kendo-react-inputs';
import { Notification, NotificationGroup } from '@progress/kendo-react-notification';
import type {
  SystemSettingCreateRequest,
  SystemSettingDto,
  SystemSettingUpdateRequest,
} from '@shared/api-types';
import { formatDateTime } from '@shared/datetime';
import { adminApiFetch } from '../../lib/adminApiClient';

export function SystemSettings() {
  const [rows, setRows] = useState<SystemSettingDto[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const selected = useMemo(
    () => rows?.find((r) => r.id === selectedId) ?? null,
    [rows, selectedId],
  );

  const [value, setValue] = useState('');
  const [seededId, setSeededId] = useState<string | null>(null);

  // Seed the edit buffer when a different setting becomes selected (render-time
  // adjust pattern, like the Email templates page).
  if (selected && selected.id !== seededId) {
    setSeededId(selected.id);
    setValue(selected.value);
  }

  const reload = async () => {
    const data = await adminApiFetch<SystemSettingDto[]>('/api/admin/system-settings');
    setRows(data);
  };

  useEffect(() => {
    let active = true;
    void (async () => {
      const data = await adminApiFetch<SystemSettingDto[]>('/api/admin/system-settings');
      if (active) setRows(data);
    })();
    return () => {
      active = false;
    };
  }, []);

  const onSave = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const body: SystemSettingUpdateRequest = { value };
      const updated = await adminApiFetch<SystemSettingDto>(
        `/api/admin/system-settings/${selected.id}`,
        { method: 'PUT', body },
      );
      setRows((current) => current?.map((r) => (r.id === updated.id ? updated : r)) ?? null);
      setToast('Saved.');
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const onNew = async () => {
    const key = window.prompt('Setting key (e.g. runner.targetVersion)');
    if (!key) return;
    try {
      const body: SystemSettingCreateRequest = { key, value: '' };
      const created = await adminApiFetch<SystemSettingDto>('/api/admin/system-settings', {
        method: 'POST',
        body,
      });
      await reload();
      setSelectedId(created.id);
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'Create failed.');
    }
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '360px 1fr', gap: 16, height: 'calc(100vh - 120px)' }}>
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h2 style={{ margin: 0 }}>System settings</h2>
          <Button size="small" onClick={() => void onNew()}>New</Button>
        </div>
        <p style={{ color: '#64748b', fontSize: 12, marginTop: 0 }}>
          Per-region key/value settings. e.g. <code>runner.targetVersion</code> sets the runner
          version this region considers current.
        </p>
        <Grid
          data={rows ?? []}
          onRowClick={(e) => setSelectedId((e.dataItem as SystemSettingDto).id)}
          style={{ height: 'calc(100vh - 240px)' }}
        >
          <GridColumn field="key" title="Key" />
          <GridColumn field="value" title="Value" />
        </Grid>
      </div>
      <div style={{ background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 2px 8px rgba(15,23,42,.06)', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {!selected && <div style={{ color: '#64748b' }}>Pick a setting from the left to edit, or add a new one.</div>}
        {selected && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 600 }}>{selected.key}</div>
                <div style={{ color: '#64748b', fontSize: 12 }}>Last updated by {selected.updatedBy} @ {formatDateTime(selected.updatedUtc)}</div>
              </div>
              <Button themeColor="primary" disabled={saving} onClick={() => void onSave()}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
            <Input
              label="Value"
              value={value}
              onChange={(e) => setValue(String(e.value ?? ''))}
              style={{ fontFamily: 'ui-monospace, Consolas, monospace', fontSize: 13 }}
            />
          </>
        )}
      </div>

      <NotificationGroup style={{ position: 'fixed', right: 16, bottom: 16 }}>
        {toast && (
          <Notification type={{ style: 'info', icon: true }} closable onClose={() => setToast(null)}>
            <span>{toast}</span>
          </Notification>
        )}
      </NotificationGroup>
    </div>
  );
}
